import type { ComponentPublicInstance, InjectionKey, ShallowRef } from 'vue'
import {
  bindEventVideoSource,
  unbindEventVideoSource,
  type EventVideoSources,
} from '~/composables/useEventVideoHlsBinding'

const MOBILE_BREAKPOINT = '(max-width: 639px)'

export const VIDEO_SHOWCASE_CLIP_DURATION_MS = 5000

export interface MagazineVideoEventShowcasePlaybackContext {
  mobileActiveCardIndex: ShallowRef<number | null>
  registerMobileVideo: (
    cardIndex: number,
    el: Element | ComponentPublicInstance | null,
    sources: EventVideoSources
  ) => void
  onDesktopCardEnter: (
    cardIndex: number,
    video: HTMLVideoElement,
    sources: EventVideoSources
  ) => void
  onDesktopCardLeave: (cardIndex: number, video: HTMLVideoElement) => void
  onMobileVideoCanPlay: (cardIndex: number) => void
  setShowcaseVisible: (visible: boolean) => void
}

export const magazineVideoEventShowcasePlaybackKey: InjectionKey<MagazineVideoEventShowcasePlaybackContext> =
  Symbol('magazineVideoEventShowcasePlayback')

export const useMagazineVideoEventShowcasePlaybackProvider = (
  videoCardIndices: ComputedRef<number[]>
) => {
  const mobilePlaylistSlot = ref(0)
  const isShowcaseVisible = ref(false)
  const mobileVideoElements: Record<number, HTMLVideoElement> = {}
  const mobileVideoSources: Record<number, EventVideoSources> = {}

  const mobileActiveCardIndex = shallowRef<number | null>(null)

  let mobileClipTimer: ReturnType<typeof setTimeout> | null = null

  const syncMobileActiveIndex = () => {
    mobileActiveCardIndex.value =
      videoCardIndices.value[mobilePlaylistSlot.value] ?? null
  }

  const isMobile = () =>
    import.meta.client && window.matchMedia(MOBILE_BREAKPOINT).matches

  const isDesktop = () => !isMobile()

  const clearMobileClipTimer = () => {
    if (mobileClipTimer) {
      clearTimeout(mobileClipTimer)
      mobileClipTimer = null
    }
  }

  const registerMobileVideo = (
    cardIndex: number,
    el: Element | ComponentPublicInstance | null,
    sources: EventVideoSources
  ) => {
    if (el instanceof HTMLVideoElement) {
      if (mobileVideoElements[cardIndex] === el) return
      mobileVideoElements[cardIndex] = el
      mobileVideoSources[cardIndex] = sources
      return
    }

    if (!el && mobileVideoElements[cardIndex]) {
      unbindEventVideoSource(mobileVideoElements[cardIndex])
      delete mobileVideoElements[cardIndex]
      delete mobileVideoSources[cardIndex]
    }
  }

  const pauseAndReset = (video: HTMLVideoElement) => {
    video.pause()
    video.currentTime = 0
    video.loop = false
  }

  const playVideo = async (
    video: HTMLVideoElement,
    sources: EventVideoSources
  ) => {
    bindEventVideoSource(video, sources)
    video.loop = false
    if (video.ended) {
      video.currentTime = 0
    }
    try {
      await video.play()
    } catch {
      // Autoplay policies — canplay handler may retry
    }
  }

  const onDesktopCardEnter = (
    _cardIndex: number,
    video: HTMLVideoElement,
    sources: EventVideoSources
  ) => {
    if (!isDesktop() || !isShowcaseVisible.value) return
    void playVideo(video, sources)
  }

  const onDesktopCardLeave = (_cardIndex: number, video: HTMLVideoElement) => {
    if (!isDesktop()) return
    pauseAndReset(video)
    unbindEventVideoSource(video)
  }

  const pauseAllMobileVideos = () => {
    Object.values(mobileVideoElements).forEach((video) => {
      pauseAndReset(video)
      unbindEventVideoSource(video)
    })
  }

  const advanceMobilePlaylist = () => {
    const total = videoCardIndices.value.length
    if (total === 0) return

    if (total > 1) {
      mobilePlaylistSlot.value = (mobilePlaylistSlot.value + 1) % total
    }

    scheduleMobilePlayback()
  }

  const scheduleMobileClipAdvance = () => {
    clearMobileClipTimer()
    mobileClipTimer = setTimeout(() => {
      if (!isMobile() || !isShowcaseVisible.value) return
      advanceMobilePlaylist()
    }, VIDEO_SHOWCASE_CLIP_DURATION_MS)
  }

  const playMobileActiveVideo = () => {
    if (
      !isMobile() ||
      !isShowcaseVisible.value ||
      videoCardIndices.value.length === 0
    ) {
      return
    }

    syncMobileActiveIndex()
    const activeIndex = mobileActiveCardIndex.value
    if (activeIndex === null) return

    Object.entries(mobileVideoElements).forEach(([idx, video]) => {
      if (Number(idx) !== activeIndex) {
        pauseAndReset(video)
        unbindEventVideoSource(video)
      }
    })

    const video = mobileVideoElements[activeIndex]
    const sources = mobileVideoSources[activeIndex]
    if (!video || !sources) return

    void playVideo(video, sources).then(() => {
      scheduleMobileClipAdvance()
    })
  }

  const scheduleMobilePlayback = () => {
    nextTick(() => playMobileActiveVideo())
  }

  const startMobilePlaylist = () => {
    if (videoCardIndices.value.length === 0) return
    mobilePlaylistSlot.value = 0
    syncMobileActiveIndex()
    scheduleMobilePlayback()
  }

  const stopMobilePlaylist = () => {
    clearMobileClipTimer()
    pauseAllMobileVideos()
    mobileActiveCardIndex.value = null
  }

  const onMobileVideoCanPlay = (cardIndex: number) => {
    if (!isMobile() || !isShowcaseVisible.value) return
    if (mobileActiveCardIndex.value !== cardIndex) return
    const video = mobileVideoElements[cardIndex]
    if (video?.paused) {
      void video.play()
    }
  }

  const setShowcaseVisible = (visible: boolean) => {
    isShowcaseVisible.value = visible
    if (!visible) {
      stopMobilePlaylist()
      return
    }
    if (isMobile()) {
      startMobilePlaylist()
    }
  }

  const bindMobileLayoutListener = () => {
    if (!import.meta.client) return

    const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT)

    const onChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        if (isShowcaseVisible.value) {
          startMobilePlaylist()
        }
        return
      }
      stopMobilePlaylist()
    }

    mediaQuery.addEventListener('change', onChange)

    onUnmounted(() => {
      mediaQuery.removeEventListener('change', onChange)
      stopMobilePlaylist()
      Object.values(mobileVideoElements).forEach(unbindEventVideoSource)
    })
  }

  let lastPlaylistKey = ''

  watch(
    () => videoCardIndices.value.join(','),
    (key) => {
      if (key === lastPlaylistKey) return
      lastPlaylistKey = key

      if (videoCardIndices.value.length === 0) {
        stopMobilePlaylist()
        return
      }
      if (mobilePlaylistSlot.value >= videoCardIndices.value.length) {
        mobilePlaylistSlot.value = 0
      }
      if (isMobile() && isShowcaseVisible.value) {
        scheduleMobilePlayback()
      }
    }
  )

  const context: MagazineVideoEventShowcasePlaybackContext = {
    mobileActiveCardIndex,
    registerMobileVideo,
    onDesktopCardEnter,
    onDesktopCardLeave,
    onMobileVideoCanPlay,
    setShowcaseVisible,
  }

  provide(magazineVideoEventShowcasePlaybackKey, context)

  onMounted(() => {
    bindMobileLayoutListener()
  })

  return context
}

export const useMagazineVideoEventShowcasePlayback = () =>
  inject(magazineVideoEventShowcasePlaybackKey)!
